import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Upload, X, MapPin } from 'lucide-react';

function ImagesUploader({ fileInputRef, uploadingImage, uploadedImages, onUpload, onTakePhoto, onRemove, formImages, setFormImages }) {
  return (
    <div className="col-span-2">
      <Label>Property Images</Label>
      <div className="mt-2 space-y-3">
        <div className="flex items-center gap-3">
          <input
            ref={fileInputRef}
            type="file"
            data-testid="property-image-file-input"
            accept="image/*"
            multiple
            onChange={onUpload}
            className="hidden"
          />
          <Button type="button" data-testid="property-upload-image-btn" onClick={() => fileInputRef.current?.click()}
            disabled={uploadingImage} variant="outline" className="rounded-full">
            <Upload className="w-4 h-4 mr-2" />
            {uploadingImage ? 'Uploading...' : 'Upload Images'}
          </Button>
          <Button type="button" data-testid="property-take-photo-btn" onClick={onTakePhoto}
            disabled={uploadingImage} variant="outline" className="rounded-full">
            <MapPin className="w-4 h-4 mr-2" />Take Photo
          </Button>
          <span className="text-xs text-muted-foreground">JPG, PNG, WebP up to 10MB</span>
        </div>
        {uploadedImages.length > 0 && (
          <div className="grid grid-cols-4 gap-2">
            {uploadedImages.map((img, idx) => (
              <div key={`${img}-${idx}`} className="relative group">
                <img src={img} alt={`Upload ${idx + 1}`} className="w-full h-24 object-cover rounded-lg border" />
                <button type="button" data-testid={`remove-image-${idx}-btn`} onClick={() => onRemove(idx)}
                  className="absolute top-1 right-1 w-6 h-6 rounded-full bg-white/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <X className="w-3 h-3 text-red-600" />
                </button>
              </div>
            ))}
          </div>
        )}
        <div>
          <Label htmlFor="images" className="text-xs text-muted-foreground">Or paste image URLs (comma separated)</Label>
          <Input id="images" data-testid="property-images-input"
            placeholder="https://example.com/image1.jpg, https://example.com/image2.jpg"
            value={formImages} onChange={(e) => setFormImages(e.target.value)} className="mt-1" />
        </div>
      </div>
    </div>
  );
}

function ApplicationFeeSection({ formData, setField }) {
  const fee = parseFloat(formData.application_fee) || 0;
  return (
    <div className="col-span-2 border-t pt-4 mt-4">
      <h3 className="font-medium mb-4">Application Fee</h3>
      <Label htmlFor="application_fee">Application Fee (USD)</Label>
      <Input id="application_fee" data-testid="property-application-fee-input"
        type="number" min="0" step="1"
        value={formData.application_fee}
        onChange={(e) => setField('application_fee', e.target.value)}
        className="mt-2" placeholder="0" />
      <p className="text-xs text-muted-foreground mt-2">
        Charged to applicants at booking. The app collects a 1% platform fee on application fees.
      </p>
      {fee > 0 && (
        <div className="mt-2 p-3 bg-muted rounded-lg text-xs">
          <div className="flex justify-between"><span>Tenant pays:</span><span className="font-medium">${fee.toFixed(2)}</span></div>
          <div className="flex justify-between text-accent"><span>Platform fee (1%):</span><span>${(fee * 0.01).toFixed(2)}</span></div>
          <div className="flex justify-between border-t border-border pt-1 mt-1 font-medium"><span>You receive:</span><span>${(fee * 0.99).toFixed(2)}</span></div>
        </div>
      )}
    </div>
  );
}

function DiscountSettings({ formData, setField, setDurationDiscount }) {
  return (
    <div className="col-span-2 border-t pt-4 mt-4">
      <h3 className="font-medium mb-4">Discount Settings</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="discount_rate">Base Discount (%)</Label>
          <Input id="discount_rate" data-testid="property-discount-input" type="number" step="0.1"
            value={formData.discount_rate} onChange={(e) => setField('discount_rate', e.target.value)} className="mt-2" />
        </div>
        <div>
          <Label htmlFor="seasonal_discount">Seasonal Discount (%)</Label>
          <Input id="seasonal_discount" data-testid="property-seasonal-discount-input" type="number" step="0.1"
            value={formData.seasonal_discount} onChange={(e) => setField('seasonal_discount', e.target.value)} className="mt-2" />
        </div>
      </div>
      <div className="mt-4">
        <Label>Duration-Based Discounts (%)</Label>
        <div className="grid grid-cols-3 gap-4 mt-2">
          {[['3months', '3 Months', 'property-3month-discount-input', 'discount_3m'],
            ['6months', '6 Months', 'property-6month-discount-input', 'discount_6m'],
            ['12months', '12 Months', 'property-12month-discount-input', 'discount_12m']].map(([key, label, tid, id]) => (
            <div key={key}>
              <Label htmlFor={id} className="text-xs">{label}</Label>
              <Input id={id} data-testid={tid} type="number" step="0.1" placeholder="0"
                value={formData.duration_discounts[key]}
                onChange={(e) => setDurationDiscount(key, e.target.value)} className="mt-1" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function PropertyFormDialog({
  open, onOpenChange, editingProperty,
  formData, setFormData,
  fileInputRef, uploadingImage, uploadedImages,
  onUploadImages, onTakePhoto, onRemoveImage,
  onSubmit,
}) {
  const setField = (k, v) => setFormData({ ...formData, [k]: v });
  const setDurationDiscount = (bucket, v) => setFormData({
    ...formData,
    duration_discounts: { ...formData.duration_discounts, [bucket]: v },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editingProperty ? 'Edit Property' : 'Add New Property'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-6 mt-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label htmlFor="title">Property Title</Label>
              <Input id="title" data-testid="property-title-input" required value={formData.title}
                onChange={(e) => setField('title', e.target.value)} className="mt-2" />
            </div>
            <div className="col-span-2">
              <Label htmlFor="description">Description</Label>
              <Textarea id="description" data-testid="property-description-input" required value={formData.description}
                onChange={(e) => setField('description', e.target.value)} className="mt-2" rows={3} />
            </div>
            <div>
              <Label htmlFor="location">Full Address</Label>
              <Input id="location" data-testid="property-location-input" required value={formData.location}
                onChange={(e) => setField('location', e.target.value)} className="mt-2" />
            </div>
            <div>
              <Label htmlFor="city">City</Label>
              <Input id="city" data-testid="property-city-input" required value={formData.city}
                onChange={(e) => setField('city', e.target.value)} className="mt-2" />
            </div>
            <div>
              <Label htmlFor="price">Monthly Rent ($)</Label>
              <Input id="price" data-testid="property-price-input" type="number" required value={formData.price}
                onChange={(e) => setField('price', e.target.value)} className="mt-2" />
            </div>
            <div>
              <Label htmlFor="property_type">Property Type</Label>
              <Select value={formData.property_type} onValueChange={(v) => setField('property_type', v)}>
                <SelectTrigger data-testid="property-type-select" className="mt-2"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="apartment">Apartment</SelectItem>
                  <SelectItem value="house">House</SelectItem>
                  <SelectItem value="studio">Studio</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="bedrooms">Bedrooms</Label>
              <Input id="bedrooms" data-testid="property-bedrooms-input" type="number" required value={formData.bedrooms}
                onChange={(e) => setField('bedrooms', e.target.value)} className="mt-2" />
            </div>
            <div>
              <Label htmlFor="bathrooms">Bathrooms</Label>
              <Input id="bathrooms" data-testid="property-bathrooms-input" type="number" required value={formData.bathrooms}
                onChange={(e) => setField('bathrooms', e.target.value)} className="mt-2" />
            </div>
            <div className="col-span-2">
              <Label htmlFor="area">Area (sqft)</Label>
              <Input id="area" data-testid="property-area-input" type="number" required value={formData.area}
                onChange={(e) => setField('area', e.target.value)} className="mt-2" />
            </div>
            <div className="col-span-2">
              <Label htmlFor="amenities">Amenities (comma separated)</Label>
              <Input id="amenities" data-testid="property-amenities-input" placeholder="e.g., Parking, WiFi, Gym"
                value={formData.amenities} onChange={(e) => setField('amenities', e.target.value)} className="mt-2" />
            </div>

            <ImagesUploader
              fileInputRef={fileInputRef}
              uploadingImage={uploadingImage}
              uploadedImages={uploadedImages}
              onUpload={onUploadImages}
              onTakePhoto={onTakePhoto}
              onRemove={onRemoveImage}
              formImages={formData.images}
              setFormImages={(v) => setField('images', v)}
            />

            <ApplicationFeeSection formData={formData} setField={setField} />
            <DiscountSettings formData={formData} setField={setField} setDurationDiscount={setDurationDiscount} />
          </div>

          <Button type="submit" data-testid="property-submit-btn"
            className="w-full rounded-full bg-primary hover:bg-primary/90 text-primary-foreground btn-hover">
            {editingProperty ? 'Update Property' : 'Add Property'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
